import React, { ComponentProps } from 'react';

import { Flags } from 'app/developer-v3/platformSchema/flags';
import {
  useActiveProfile,
  useCurrentVersionReadOnlyReason,
  useFeatureFlag,
  useIntegrationMaintenanceConfig,
  useIntegrationVersionConfig,
} from 'app/developer-v3/hooks';
import LoadingBlock from 'app/common/components/LoadingBlock';

import Settings from './Settings';

type Props = {
  flags: Flags;
  integrationId: number;
  integrationVersion: string;
  isGUIIntegration: boolean;
  saveFlags: (flags: Flags) => Promise<void>;
} & ComponentProps<typeof Settings>;

// TODO: Remove either this wrapper or its parent Container comonent once all
// settings are migrated away from Entites CliApp and CliAppDefinition. Ideally,
// we only use hooks useQueryImplementation,
// useQueryIntegrationVersionConfig, and
// useQueryIntegrationMaintenanceConfig along with their corresponding mutation
// hooks to fetch and save settings. That way we can remove the Entities layer
// entirely.
//
// This wrapper exists to bridge the gap between the two methods of handling
// settings. Its parent Container component fetches the definition flags by
// Entities CliApp and CliAPpDefinition. Meanwhile, this wrapper fetches the
// version config by using the useIntegrationVersionConfig hook. The
// presentational component Settings should be agnostic of where the data comes
// from. So this wrapper passes down the necessary data and handles saving
// both the definition flags and the version config when the user saves the
// settings.
function Wrapper({
  flags,
  integrationId,
  integrationVersion,
  isGUIIntegration,
  isSaving: isSavingFlags,
  saveFlags,
  ...props
}: Props) {
  const {
    integrationMaintenanceConfig,
    isLoading: isLoadingIntegrationMaintenanceConfig,
    isUpdating: isUpdatingIntegrationMaintenanceConfig,
    update: updateIntegrationMaintenanceConfig,
  } = useIntegrationMaintenanceConfig({
    integrationId,
    optionsUseMutationUpdateIntegrationMaintenanceConfig: {
      onSuccess: () => undefined,
    },
  });
  const {
    integrationVersionConfig,
    isLoading: isLoadingIntegrationVersionConfig,
    isUpdating: isUpdatingIntegrationVersionConfig,
    update: updateIntegrationVersionConfig,
  } = useIntegrationVersionConfig({
    integrationId,
    integrationVersion,
    optionsUseQueryIntegrationVersionConfig: {},
    // We provide a no-op onSuccess to prevent two notifications from
    // showing up for UI integrations since saving definition flags also
    // triggers a notification. We don't save definition flags for CLI
    // integrations because they're read-only in the UI. That prevents us
    // from having double notifications for CLI integrations.
    optionsUseMutationUpdateIntegrationVersionConfig: isGUIIntegration
      ? { onSuccess: () => undefined }
      : undefined,
  });

  const { activeProfile } = useActiveProfile();
  const {
    isOn: isIntegrationMaintenanceToggleVisible,
    isReady: isIntegrationMaintenanceToggleReady,
  } = useFeatureFlag(
    'integration_maintenance_toggle_visible',
    String(activeProfile?.id)
  );

  const isVersionReadOnly = !!useCurrentVersionReadOnlyReason();

  return isLoadingIntegrationVersionConfig ||
    isLoadingIntegrationMaintenanceConfig ||
    !isIntegrationMaintenanceToggleReady ? (
    <LoadingBlock />
  ) : (
    <Settings
      {...props}
      isGUIIntegration={isGUIIntegration}
      isInitialIntegrationMaintenanceConfigInternalMaintenanceAllowed={
        integrationMaintenanceConfig?.allowInternalMaintenance ?? true
      }
      isInitialSkipHttpPatch={flags.skipHttpPatch}
      isInitialSkipThrowForStatus={flags.skipThrowForStatus}
      isInitialStaticIPEnabled={
        integrationVersionConfig?.isStaticIPEnabled ?? false
      }
      isIntegrationMaintenanceConfigEditable={
        integrationMaintenanceConfig?.isEditable ?? false
      }
      isIntegrationMaintenanceToggleVisible={
        isIntegrationMaintenanceToggleVisible
      }
      isSaving={
        isSavingFlags ||
        isUpdatingIntegrationVersionConfig ||
        isUpdatingIntegrationMaintenanceConfig
      }
      onSave={async ({
        isStaticIPEnabled,
        isSkipHttpPatch,
        isSkipThrowForStatus,
        isIntegrationMaintenanceConfigInternalMaintenanceAllowed,
      }) => {
        await Promise.allSettled([
          ...(isVersionReadOnly
            ? []
            : [
                updateIntegrationVersionConfig({
                  ...(integrationVersionConfig ?? { isStaticIPEnabled: false }),
                  isStaticIPEnabled,
                }),
              ]),
          ...(isVersionReadOnly || !isGUIIntegration
            ? []
            : [
                saveFlags({
                  skipHttpPatch: isSkipHttpPatch,
                  skipThrowForStatus: isSkipThrowForStatus,
                }),
              ]),
          ...(integrationMaintenanceConfig.isEditable
            ? [
                updateIntegrationMaintenanceConfig({
                  allowInternalMaintenance: isIntegrationMaintenanceConfigInternalMaintenanceAllowed,
                }),
              ]
            : []),
        ]);
      }}
    />
  );
}

export default Wrapper;
